<template>
  <v-card
    class="pa-4 overflow-y-auto"
    :height="formData.units === 'dynamic' ? 700 : 'auto'"
    style="border-radius: 10px"
  >
    <div class="d-flex pa-4">
      <div
        class="flex text-center justify-center font-weight-medium text-subtitle-1"
      >
        {{ title }}
      </div>
    </div>
    <v-form ref="form">
      <v-text-field
        v-model.trim="formData.name"
        dense
        outlined
        :rules="[required]"
        :label="$t('Type')"
      />
      <v-combobox
        v-model="formData.category"
        :items="categories"
        dense
        outlined
        :rules="[required]"
        :label="$t('Category')"
      />
      <v-select
        v-if="!isEditMode"
        v-model="formData.expense_role"
        label="Expense role"
        outlined
        :rules="[required]"
        :items="expenseRoles"
        item-text="name"
        item-value="value"
        dense
      />
      <v-select
        v-model="formData.units"
        outlined
        dense
        :items="expenseUnits"
        label="Unit"
        :rules="unitsRules"
      />
      <v-text-field
        v-if="formData.units !== 'dynamic'"
        v-model="formData.amount"
        type="number"
        outlined
        dense
        :label="$t('Amount')"
        required
      />
      <enhanced-formula-builder
        v-else
        v-model="formData.quantifier"
        label="Quantifier Formula"
        :variables="formulaVariables"
        :rules="[required]"
      />
      <v-text-field
        v-model="formData.vat_rate"
        type="number"
        outlined
        dense
        :label="$t('VAT Rate (%)')"
        :rules="vatRateRules"
        step="0.01"
        min="0"
        max="100"
        :hint="$t('Enter VAT rate as a percentage (e.g., 20 for 20%)')"
        persistent-hint
      />
      <v-divider class="my-4"></v-divider>
      <div class="text-subtitle-2 mb-2">
        Management Fee Configuration (Optional)
      </div>
      <v-select
        v-model="formData.mgmt_fee_units"
        outlined
        dense
        :items="mgmtFeeUnits"
        label="Management Fee Type"
        clearable
      />
      <v-text-field
        v-if="formData.mgmt_fee_units === 'fixed'"
        v-model="formData.mgmt_fee_amount"
        type="number"
        outlined
        dense
        :label="$t('Management Fee Amount')"
        step="0.01"
        min="0"
      />
      <v-text-field
        v-else-if="formData.mgmt_fee_units === 'percentage'"
        v-model="formData.mgmt_fee_amount"
        type="number"
        outlined
        dense
        :label="$t('Management Fee Percentage')"
        step="0.01"
        min="0"
        max="100"
        :hint="$t('Enter percentage (e.g., 10 for 10%)')"
        persistent-hint
      />
      <v-text-field
        v-if="formData.mgmt_fee_units"
        v-model="formData.mgmt_vat_rate"
        type="number"
        outlined
        dense
        :label="$t('Management Fee VAT Rate (%)')"
        :rules="vatRateRules"
        step="0.01"
        min="0"
        max="100"
        :hint="$t('VAT rate for management fee')"
        persistent-hint
      />
      <filters-select
        v-if="businessModelId"
        :value.sync="formData.channels"
        label="Channels"
        :items="channelsItems"
        :selection-func="item => item.text"
        :messages="['Leave empty to apply to all channels']"
      />
      <v-card-actions class="justify-end">
        <v-btn color="primary" outlined @click="onClose">Cancel</v-btn>
        <v-btn color="primary" @click="submit">Submit</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import FormattersMixin from 'components/mixins/formatters-mixin'
import { mapActions, mapState, mapGetters } from 'vuex'
import EnhancedFormulaBuilder from 'components/common/enhanced-formula-builder.vue'
import { formulaVariables } from '@/consts'
import FiltersSelect from 'components/form-fields/filters-select'

export default {
  name: 'ExpenseTypeModal',
  components: { EnhancedFormulaBuilder, FiltersSelect },
  mixins: [FormRulesMixin, FormattersMixin],
  props: [
    'categories',
    'expenseRoles',
    'afterSave',
    'expenseType',
    'businessModelId',
    'businessModel',
  ],
  data: function () {
    return {
      formData: {},
    }
  },
  computed: {
    ...mapGetters('reservation', ['channelsItems']),
    title() {
      return this.isEditMode
        ? this.$t('Edit Expense Type')
        : this.$t('New Expense Type')
    },
    isEditMode() {
      return !!this.expenseType
    },
    formulaVariables() {
      const baseVariables = formulaVariables

      if (this.businessModel && this.businessModel.custom_fields) {
        const customFields = this.businessModel.custom_fields.map(v => ({
          description: v.description,
          value: v.variable_name,
          category: 'Custom Variables',
        }))
        return baseVariables.concat(customFields)
      }

      return baseVariables
    },
    expenseUnits() {
      return [
        { text: `Fixed (${this.currencySign})`, value: 'fixed' },
        { text: 'Dynamic', value: 'dynamic' },
      ]
    },
    mgmtFeeUnits() {
      return [
        { text: `Fixed Amount (${this.currencySign})`, value: 'fixed' },
        { text: 'Percentage (%)', value: 'percentage' },
      ]
    },
    unitsRules() {
      return [
        v => {
          if (this.formData.amount !== '' && this.formData.amount !== null) {
            return v ? true : 'Unit is required when amount is specified'
          }
          return true
        },
      ]
    },
    vatRateRules() {
      return [
        v => {
          if (v !== null && v !== '' && v !== undefined) {
            const num = parseFloat(v)
            if (isNaN(num)) return 'VAT rate must be a valid number'
            if (num < 0) return 'VAT rate cannot be negative'
            if (num > 100) return 'VAT rate cannot exceed 100%'
          }
          return true
        },
      ]
    },
  },
  watch: {
    expenseType: {
      handler(newVal) {
        this.initializeFormData()
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('expenseTypes', [
      'createNewExpenseType',
      'updateExpenseType',
    ]),
    initializeFormData() {
      // Create a deep copy of the expense type data to avoid in-place mutations
      if (this.expenseType) {
        this.formData = {
          ...this.expenseType,
          // Ensure channels is properly copied as an array
          channels: this.expenseType.channels
            ? [...this.expenseType.channels]
            : [],
        }
      } else {
        this.formData = {
          name: '',
          category: '',
          expense_role: '',
          units: 'fixed',
          amount: '',
          quantifier: '',
          vat_rate: 0.0,
          mgmt_fee_units: null,
          mgmt_fee_amount: '',
          mgmt_vat_rate: 0.0,
          channels: [],
        }
      }
    },
    submit() {
      if (this.$refs.form.validate()) {
        const action = this.isEditMode
          ? 'updateExpenseType'
          : 'createNewExpenseType'

        // Create a clean copy of form data for submission
        const submitData = {
          ...this.formData,
          business_model_id: this.businessModelId,
        }

        this[action](submitData).then(() => {
          this.afterSave()
          this.onClose()
        })
      }
    },
    onClose() {
      // Reset form validation
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }

      // Re-initialize form data to original state
      this.initializeFormData()

      // Close modal
      this.$store.commit('hideModal')
    },
  },
}
</script>

<style scoped></style>
